import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Search, ArrowLeft, AlertCircle } from "lucide-react";
import logo from "../assets/logo.svg";

export default function NotFound() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl w-full text-center"
      >
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 blur-xl animate-pulse" />
            <div className="relative w-16 h-16 flex items-center justify-center">
              <img src={logo} alt="CamPulse logo" className="w-16 h-16" />
            </div>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-foreground tracking-tight mb-12">
          <span className="text-primary font-semibold">Cam</span>
          <span className="font-light">Pulse</span>
        </h1>

        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-red-500/10 border-2 border-red-500/20 flex items-center justify-center">
              <AlertCircle className="w-12 h-12 text-red-400" />
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <h2 className="text-6xl sm:text-7xl font-bold text-foreground mb-4">
            404
          </h2>
          <h3 className="text-2xl sm:text-3xl font-semibold text-foreground mb-4">
            Page Not Found
          </h3>
          <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
            The page you're looking for doesn't exist or has been moved to
            another location.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all hover:scale-105 shadow-lg hover:shadow-xl"
          >
            <Home className="w-5 h-5" />
            Go to Dashboard
          </Link>

          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 px-6 py-3 bg-card border border-border/50 text-foreground rounded-lg font-medium hover:bg-muted transition-all hover:scale-105 cursor-pointer"
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-12 pt-8 border-t border-border/50"
        >
          <p className="text-sm text-muted-foreground mb-4">
            Need help? Here are some helpful links:
          </p>
          <div className="flex flex-wrap gap-4 justify-center text-sm">
            <Link
              to="/dashboard"
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              <Home className="w-4 h-4" />
              Service Desk
            </Link>
            <Link
              to="/dashboard/new-ticket"
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              <Search className="w-4 h-4" />
              Create Incident
            </Link>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-12 text-xs text-muted-foreground"
        >
          © 2026 <span className="text-primary font-semibold">Cam</span>
          <span className="font-extralight">Pulse</span>. The heartbeat of
          campus IT operations.
        </motion.p>
      </motion.div>
    </div>
  );
}
